// Route preloader for lazy-loaded chunks
// Maps routes to their import functions for preloading on hover

type ImportFn = () => Promise<unknown>;

const routeImports: Record<string, ImportFn> = {
  // Dashboard
  '/': () => import('@/pages/Dashboard'),
  '/dashboard': () => import('@/pages/Dashboard'),

  // Onboarding
  '/onboarding': () => import('@/pages/onboarding/PlanLaunch'),
  '/onboarding/index': () => import('@/pages/onboarding/OnboardingIndex'),
  '/onboarding/validations': () => import('@/pages/onboarding/Validations'),
  '/onboarding/generate': () => import('@/pages/onboarding/Generate'),

  // Unified Pages
  '/messages': () => import('@/pages/unified/MessagesUnified'),
  '/agents': () => import('@/pages/unified/AgentsUnified'),

  // Path 1
  '/path1/import': () => import('@/pages/path1/ImportContacts'),
  '/path1/classify': () => import('@/pages/path1/Classify'),
  '/path1/messages': () => import('@/pages/unified/MessagesUnified'),
  '/path1/agent': () => import('@/pages/unified/AgentsUnified'),
  '/path1/launch': () => import('@/pages/path1/Launch'),
  '/path1/crm': () => import('@/pages/path1/CRM'),
  '/path1/learning': () => import('@/pages/path1/Learning'),

  // Path 2
  '/path2/profile': () => import('@/pages/path2/ProfileHub'),
  '/path2/profile-optimizer': () => import('@/pages/path2/ProfileOptimizer'),
  '/path2/photo-generator': () => import('@/pages/path2/PhotoGenerator'),
  '/path2/reels': () => import('@/pages/path2/CopyHackingReels'),
  '/path2/messages': () => import('@/pages/unified/MessagesUnified'),
  '/path2/comments': () => import('@/pages/unified/MessagesUnified'),

  // Library
  '/library/templates': () => import('@/pages/unified/MessagesUnified'),
  '/library/agents': () => import('@/pages/unified/AgentsUnified'),
  '/library/materials': () => import('@/pages/MaterialCorporativo'),
  '/library/tutorials': () => import('@/pages/Tutorials'),

  // Data
  '/data/dashboard': () => import('@/pages/Dashboard'),
  '/data/campaigns': () => import('@/pages/connector/Campaigns'),

  // Settings
  '/settings': () => import('@/pages/Settings'),
  '/settings/company': () => import('@/pages/Settings'),
  '/settings/team': () => import('@/pages/Settings'),

  // Connector
  '/connector/integration': () => import('@/pages/connector/Integration'),
  '/connector/templates': () => import('@/pages/connector/Templates'),
  '/connector/campaigns': () => import('@/pages/connector/Campaigns'),
  '/connector/lists': () => import('@/pages/connector/Lists'),
  '/connector/funnels': () => import('@/pages/connector/Funnels'),

  // Setter
  '/setter/whatsapp': () => import('@/pages/setter/WhatsApp'),
  '/setter/whatsapp-templates': () => import('@/pages/WhatsAppTemplates'),
  '/setter/agents': () => import('@/pages/setter/Agents'),
  '/setter/agents/create': () => import('@/pages/setter/CreateAgent'),
  '/setter/blacklist': () => import('@/pages/setter/Blacklist'),
  '/setter-bayleis-conexion': () => import('@/pages/setter/BayleisConexion'),

  // CRM
  '/chat': () => import('@/pages/CrmChat'),
  '/crm/dashboard': () => import('@/pages/CrmUnified'),
  '/crm/capital-relacional': () => import('@/pages/CrmCapitalRelacional'),
  '/crm/asignar-agentes': () => import('@/pages/AsignarAgentes'),

  // Calendar
  '/calendar': () => import('@/pages/Calendar'),
  '/calendar/new-event': () => import('@/pages/CreateEvent'),
  '/calendar/meetings': () => import('@/pages/ReunionesAgendadas'),

  // Profile & Misc
  '/my-profile': () => import('@/pages/MyProfile'),
  '/notifications': () => import('@/pages/Notifications'),
  '/about': () => import('@/pages/About'),
  '/tutorials': () => import('@/pages/Tutorials'),

  // Admin
  '/admin/prompts': () => import('@/pages/admin/SystemPromptsManager'),
  '/admin/knowledge': () => import('@/pages/admin/KnowledgeBaseManager'),
};

// Track preloaded routes to avoid duplicate requests
const preloadedRoutes = new Set<string>();

/**
 * Preload a route's chunk on hover
 * Only preloads once per route per session
 */
export function preloadRoute(path: string): void {
  // Skip if already preloaded
  if (preloadedRoutes.has(path)) return;

  const importFn = routeImports[path];
  if (importFn) {
    preloadedRoutes.add(path);
    // Use requestIdleCallback for non-blocking preload
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => importFn(), { timeout: 2000 });
    } else {
      // Fallback for Safari
      setTimeout(() => importFn(), 100);
    }
  }
}

/**
 * Preload multiple routes (for groups/dropdowns)
 */
export function preloadRoutes(paths: string[]): void {
  paths.forEach(preloadRoute);
}

/**
 * OPTIMIZED: Critical routes to preload during idle time
 * These are the most commonly accessed routes after login
 */
const CRITICAL_ROUTES = [
  '/dashboard',
  '/chat',
  '/crm/dashboard',
  '/onboarding',
  '/setter/whatsapp',
  '/calendar'
];

/**
 * Preload critical routes during browser idle time
 * Call this after initial page load to warm the cache
 */
export function preloadCriticalRoutes(): void {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      CRITICAL_ROUTES.forEach((route, index) => {
        // Stagger preloads to avoid overwhelming the browser
        setTimeout(() => preloadRoute(route), index * 500);
      });
    }, { timeout: 5000 });
  } else {
    // Fallback: preload after 2 seconds
    setTimeout(() => {
      CRITICAL_ROUTES.forEach((route, index) => {
        setTimeout(() => preloadRoute(route), index * 500);
      });
    }, 2000);
  }
}

/**
 * Check if a route has been preloaded
 */
export function isRoutePreloaded(path: string): boolean {
  return preloadedRoutes.has(path);
}

/**
 * Get count of preloaded routes (for debugging)
 */
export function getPreloadedCount(): number {
  return preloadedRoutes.size;
}
